import { type IAsyncTaskStatus, type IDataObjectIdentity, type Query, queryBuilder } from '@inpart-io/devtools-iap';
import { CASCADE_DELETE_DATA_OBJECT, GET_BY_ID, RECEIVE_DATA_OBJECTS, STORE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import {
    type AssetProfile,
    DataType,
    deleteExecutionManager,
    type IBrief,
    type IDeal,
    type IDeleteAsyncExecStatusResult,
    type IInfogroupContainer,
    type ILead,
    type ILeadAssetProfile,
    type ILeadCompanyProfile,
    type ILeadConsumerHealthProfile,
    type ILeadLinkedCompany,
    type ILeadOriginInPartCampaignProfile,
    type ILeadProfile,
    type ILeadTechnologyProfile,
    type IPartnerOpportunityInfo,
    type IPartnerPushLeadStatus,
    leadsManager,
    meetingsManager,
    partnerServiceManager
} from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { getDobjId, isDataObject } from '@/services/data-object-helper';
import { BRIEF_MODULE } from '@/store/brief-module';
import { DEAL_MODULE } from '@/store/deal-module';
import { LEAD_LINKED_COMPANIES_MODULE } from '@/store/lead-linked-companies-module';

import { LEAD_ASSET_PROFILE_MODULE } from '../lead-asset-profile-module';
import { LEAD_COMPANY_PROFILE_MODULE } from '../lead-company-profile-module';
import { LEAD_CONSUMER_HEALTH_PROFILE_MODULE } from '../lead-consumer-health-profile-module';
import { LEAD_ORIGIN_IN_PART_CAMPAIGN_PROFILE_MODULE } from '../lead-origin-in-part-profile-module';
import { LEAD_PROFILE_MODULE } from '../lead-profile-module';
import { LEAD_TECHNOLOGY_PROFILE_MODULE } from '../lead-technology-profile-module';
import { MANDATORY_FIELD_CONFIGURATION_MODULE } from '../mandatory-field-configuration-module';
import { RETRIEVE_MANDATORY_CONFIGURATIONS } from '../mandatory-field-configuration-module/actions';
import { DEFAULT_KEY } from './constants';
import {
    RECEIVE_LEAD_AGGREGATION_BY_BRIEF_STATUS,
    RECEIVE_LEAD_AGGREGATION_BY_BRIEF_TYPE,
    RECEIVE_LEAD_AGGREGATION_BY_ORIGIN_ENTITY,
    RECEIVE_LEAD_AGGREGATION_BY_STAGE,
    RECEIVE_LEAD_AGGREGATION_BY_STATUS,
    RECEIVE_LEAD_AGGREGATION_BY_TYPE,
    UPDATE_AGGREGATION_OF_STATUS,
    UPDATE_AGGREGATION_OF_TYPE
} from './mutations';

export const CREATE_LEAD = 'createLead';
export const PAGINATION = 'pagination';
export const COPY_LEAD = 'copyLead';
export const MOVE_LEAD = 'moveLead';
export const COUNT_LEADS_BY_TYPE = 'countLeadsByType';
export const COUNT_LEADS_TYPE_BY_BRIEF = 'countLeadsTypeByBrief';
export const COUNT_LEADS_BY_STAGE = 'countLeadsByStage';
export const COUNT_LEADS_BY_STATUS = 'countLeadsByStatus';
export const COUNT_LEADS_STATUS_BY_BRIEF = 'countLeadsStatusByBrief';
export const COUNT_LEADS_BY_ORIGIN_ENTITY = 'countLeadsByOriginEntity';
export const DECREASE_LEAD_COUNT = 'decreaseLeadCount';
export const INCREASE_LEAD_COUNT = 'increaseLeadCount';
export const DECREASE_LEAD_COUNT_BY_TYPE = 'decreaseLeadCountByType';
export const INCREASE_LEAD_COUNT_BY_TYPE = 'increaseLeadCountByType';
export const CASCADE_DELETE_LEAD = 'cascadeDeleteLead';
export const COUNT_MEETINGS = 'countMeetings';
export const INCREMENT_MEETINGS = 'incrementMeetings';
export const DECREMENT_MEETINGS = 'decrementMeetings';

export const CHANGE_ASSET_COMPANY = 'changeAssetCompany';
export const CHANGE_DEAL = 'changeDeal';
export const DRAFT_LEAD = 'setStatusDraft';
export const TRIAGE_LEAD = 'setStatusTriage';
export const QUALIFY_LEAD = 'setStatusQualify';
export const DISQUALIFY_LEAD = 'setStatusDisqualify';
export const ON_HOLD_LEAD = 'setStatusOnHold';
export const DEAL_LEAD = 'setStatusDeal';
export const CLOSE_LEAD = 'setStatusClose';

export const RETRIEVE_PUSH_LEAD = 'retrievePushLeadStatus';
export const RETRIEVE_OPPORTUNITY_LINK = 'retrieveOpportunityLink';

export const PUSH_LEAD = 'pushLead';

const DEFAULT_DELAY_IN_MS = 1000;

export const actions: ActionTree<ILead, unknown> = {
    async [CREATE_LEAD](context, { dataObject, properties }: { dataObject: ILead; properties?: string }): Promise<number[]> {
        const leads: ILead[] = await leadsManager.createLead(dataObject, { properties });
        return leads.map((lead) => {
            return lead.dobjId;
        });
    },

    async [CHANGE_ASSET_COMPANY](
        context,
        { leadId, companyId, properties }: { leadId: number; companyId: number; updateAssetCompany: boolean; properties?: string }
    ): Promise<void> {
        const result = await leadsManager.changeAssetCompany(leadId, { companyId }, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },

    async [RETRIEVE_MANDATORY_CONFIGURATIONS](context, { dobjId }: { dobjId: number }): Promise<number[]> {
        const configs = await leadsManager.getMandatoryConfiguration(dobjId);
        void context.dispatch(`${MANDATORY_FIELD_CONFIGURATION_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: configs }, { root: true });
        return configs.map((config) => {
            return config.dobjId;
        });
    },

    async [COPY_LEAD](
        context,
        {
            leadId,
            targetBriefId,
            title,
            tags,
            files,
            emails,
            publications,
            links,
            properties
        }: {
            leadId: number;
            targetBriefId: number;
            title: string;
            tags: number[];
            files: boolean;
            emails: boolean;
            publications: boolean;
            links: boolean;
            properties?: string;
        }
    ): Promise<number | undefined> {
        const statusId: string = await leadsManager.copyToBrief(leadId, targetBriefId, title, tags, files, emails, publications, links, { properties });
        const result = await leadsManager.getExecutionManager().waitForStatus(statusId);
        return result?.data?.clonedLeadId;
    },
    async [MOVE_LEAD](
        context,
        { leadId, targetBriefId, properties }: { leadId: number; targetBriefId: number; properties?: string }
    ): Promise<number | undefined> {
        const result = await leadsManager.moveToBrief(leadId, targetBriefId, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
        return result.dobjId;
    },
    async [CASCADE_DELETE_LEAD](context, { dobjId }: { dobjId: number }) {
        const status: IAsyncTaskStatus = await context.dispatch(CASCADE_DELETE_DATA_OBJECT, { dobjId });

        let count = 0;
        if (status?.statusId !== undefined) {
            const interval = setInterval(() => {
                const handler = async (): Promise<void> => {
                    count++;
                    const result: IDeleteAsyncExecStatusResult = await deleteExecutionManager.checkStatus(status.statusId);
                    const isSuccess = result.state === 'SUCCESSFUL';
                    const isRunning = !isSuccess && result.state !== 'FAILED';
                    if (!isRunning || count > 10) {
                        clearInterval(interval);
                    }
                };
                handler().catch((error) => {
                    console.debug(error);
                });
            }, DEFAULT_DELAY_IN_MS);
        }
    },
    async [COUNT_LEADS_BY_TYPE](context, { query, key = DEFAULT_KEY }: { query?: Query; key?: string }) {
        try {
            const aggregationResult = await leadsManager.countTypeByQuery(query);
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_TYPE, { key, aggregationResult });
        } catch (error) {
            console.error(error);
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_TYPE, { key, aggregationResult: undefined });
        }
    },
    [DECREASE_LEAD_COUNT_BY_TYPE](context, { type, count = 1, key = DEFAULT_KEY }: { type?: string; key?: string; count?: number }) {
        context.commit(UPDATE_AGGREGATION_OF_TYPE, { type, key, count: Math.abs(count) * -1 });
    },
    [INCREASE_LEAD_COUNT_BY_TYPE](context, { type, count = 1, key = DEFAULT_KEY }: { type?: string; key?: string; count?: number }) {
        context.commit(UPDATE_AGGREGATION_OF_TYPE, { type, key, count: Math.abs(count) });
    },
    async [COUNT_LEADS_TYPE_BY_BRIEF](context, { query, briefs = [] }: { query?: Query; briefs?: number[] }) {
        try {
            const aggregationResult = await leadsManager.countTypeByBrief(query, briefs.length);
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_BRIEF_TYPE, { aggregationResult, briefs });
        } catch (error) {
            console.error(error);
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_BRIEF_TYPE, { aggregationResult: undefined });
        }
    },
    async [COUNT_LEADS_BY_STAGE](context, { query, key = DEFAULT_KEY }: { query?: Query; key?: string }) {
        try {
            const aggregationResult = await leadsManager.countStageByQuery(query);
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_STAGE, { key, aggregationResult });
        } catch (error) {
            console.error(error);
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_STAGE, { key, aggregationResult: undefined });
        }
    },
    async [COUNT_LEADS_BY_STATUS](context, { query, key = DEFAULT_KEY }: { query?: Query; key?: string }) {
        try {
            const aggregationResult = await leadsManager.countLeadStatusByQuery(query);
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_STATUS, { key, aggregationResult });
        } catch (error) {
            console.error(error);
            // The endpoint is throwing an error 555 if the count is 0.
            // This should be fixed in future versions of the IAP's aggregations
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_STATUS, { key, aggregationResult: undefined });
        }
    },
    async [COUNT_LEADS_STATUS_BY_BRIEF](context, { query, briefs = [] }: { query: Query; briefs?: number[] }) {
        try {
            const aggregationResult = await leadsManager.countLeadsStatusByBrief(query, briefs.length);
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_BRIEF_STATUS, { aggregationResult, briefs });
        } catch (error) {
            console.error(error);
            // The endpoint is throwing an error 555 if the count is 0.
            // This should be fixed in future versions of the IAP's aggregations
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_BRIEF_STATUS, { aggregationResult: undefined });
        }
    },
    async [COUNT_LEADS_BY_ORIGIN_ENTITY](context, { query, key = DEFAULT_KEY }: { query?: Query; key?: string }) {
        try {
            const aggregationResult = await leadsManager.countLeadsByOriginEntity(query);
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_ORIGIN_ENTITY, { key, aggregationResult });
        } catch (error) {
            console.error(error);
            context.commit(RECEIVE_LEAD_AGGREGATION_BY_ORIGIN_ENTITY, { key, aggregationResult: undefined });
        }
    },
    [DECREASE_LEAD_COUNT](context, { status, count = 1, key = DEFAULT_KEY }: { status?: string; key?: string; count?: number }) {
        context.commit(UPDATE_AGGREGATION_OF_STATUS, { status, key, count: Math.abs(count) * -1 });
    },
    [INCREASE_LEAD_COUNT](context, { status, count = 1, key = DEFAULT_KEY }: { status?: string; key?: string; count?: number }) {
        context.commit(UPDATE_AGGREGATION_OF_STATUS, { status, key, count: Math.abs(count) });
    },
    async [DRAFT_LEAD](context, { leadId, properties }: { leadId: number; properties?: string }) {
        const result = await leadsManager.changeStatusToDraft(leadId, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },
    async [TRIAGE_LEAD](context, { leadId, properties }: { leadId: number; properties?: string }) {
        const result = await leadsManager.changeStatusToTriage(leadId, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },
    async [CHANGE_DEAL](
        context,
        {
            leadId,
            statusId,
            dealTypeId,
            properties,
            updateWorkflow
        }: { leadId: number; statusId: number; dealTypeId?: string; properties?: string; updateWorkflow?: boolean }
    ) {
        const result = await leadsManager.changeDealStatus(leadId, statusId, dealTypeId, { properties }, updateWorkflow);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },
    async [QUALIFY_LEAD](context, { leadId, comment, properties }: { leadId: number; comment: string; properties?: string }) {
        const result = await leadsManager.changeStatusToQualified(leadId, comment, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },
    async [DISQUALIFY_LEAD](
        context,
        { leadId, reason, reasonDetail, date, properties }: { leadId: number; reason: number; reasonDetail: string; date: string; properties?: string }
    ) {
        const result = await leadsManager.changeStatusToDisqualified(leadId, reason, reasonDetail, date, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },
    async [ON_HOLD_LEAD](
        context,
        {
            leadId,
            reason,
            reasonDetail,
            date,
            dueDate,
            properties
        }: { leadId: number; reason: number; reasonDetail: string; date: string; dueDate: string; properties?: string }
    ) {
        const result = await leadsManager.changeStatusToOnHold(leadId, reason, reasonDetail, date, dueDate, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },
    async [DEAL_LEAD](
        context,
        {
            leadId,
            dealType,
            activeDealStageId,
            updateWorkflow,
            properties
        }: { leadId: number; dealType: string; activeDealStageId: number; updateWorkflow: boolean; properties?: string }
    ) {
        const result = await leadsManager.changeStatusToDeal(leadId, dealType, activeDealStageId, updateWorkflow, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },
    async [CLOSE_LEAD](context, { leadId, properties }: { leadId: number; properties?: string }) {
        const result = await leadsManager.changeStatusToClosed(leadId, { properties });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
    },
    async [RETRIEVE_PUSH_LEAD](context, { leadId, forceUpdate }: { leadId: number; forceUpdate?: boolean }): Promise<IPartnerPushLeadStatus> {
        const result = await partnerServiceManager.getPartnerPushLeadStatus(leadId, forceUpdate);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
        return result;
    },
    async [RETRIEVE_OPPORTUNITY_LINK](context, { leadId }: { leadId: number }): Promise<IPartnerOpportunityInfo> {
        const result = await partnerServiceManager.getOpportunityInfo(leadId);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
        return result;
    },
    async [PUSH_LEAD](context, { leadId }: { leadId: number; properties?: string }): Promise<IPartnerPushLeadStatus> {
        const result = await partnerServiceManager.pushLead(leadId);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
        return result;
    },

    async [COUNT_MEETINGS](context, { leadId }: { leadId: number }): Promise<void> {
        const query = queryBuilder.build(queryBuilder.op.or(queryBuilder.cmp.isIn('projects', [leadId])));
        const meetingsNb = await meetingsManager.count(query);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [{ dobjId: leadId, meetingsNb }] });
    },
    [INCREMENT_MEETINGS](context, { leadId }: { leadId: number }): void {
        const lead: ILead = context.getters[GET_BY_ID](leadId);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [{ dobjId: leadId, meetingsNb: ((lead.meetingsNb as number) ?? 0) + 1 }] });
    },
    [DECREMENT_MEETINGS](context, { leadId }: { leadId: number }): void {
        const lead: ILead = context.getters[GET_BY_ID](leadId);

        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [{ dobjId: leadId, meetingsNb: ((lead.meetingsNb as number) ?? 0) - 1 }] });
    },
    [STORE_DATA_OBJECTS](context, { dataObjectList }: { dataObjectList: ILead[] }) {
        const leads: ILead[] = [];
        const briefs: IBrief[] = [];
        const deals: IDeal[] = [];
        const assetProfiles: ILeadProfile[] = [];
        const inPartProfiles: ILeadOriginInPartCampaignProfile[] = [];
        const leadAssetProfiles: ILeadAssetProfile[] = [];
        const leadTechnologyProfiles: ILeadTechnologyProfile[] = [];
        const leadConsumerHealthProfiles: ILeadConsumerHealthProfile[] = [];
        const leadCompanyProfiles: ILeadCompanyProfile[] = [];
        const relatedCompanies: ILeadLinkedCompany[] = [];

        dataObjectList.forEach((lead) => {
            let _lead = Object.assign({}, lead);

            // Extract Brief object if necessary
            if (lead.brief !== null && lead.brief !== undefined && typeof lead.brief !== 'number') {
                const brief = Object.assign({}, lead.brief);
                _lead = Object.assign({}, lead, { brief: brief.dobjId });
                briefs.push(brief);
            }

            // Extract Deal object if necessary
            if (lead?.['@deal']?.length > 0) {
                const deal = lead['@deal'][0];
                deals.push(deal);
                _lead = Object.assign({}, _lead, { deal: deal.dobjId, '@deal': undefined });
            }

            // Extract Related companies
            if (lead?.['@leadCompanies']?.length > 0) {
                const links: ILeadLinkedCompany[] = lead['@leadCompanies'] as ILeadLinkedCompany[];
                const linkIds = links.map((value) => {
                    if (typeof value !== 'number') {
                        return value.dobjId;
                    }
                    return value;
                });
                relatedCompanies.push(...links);
                _lead = Object.assign({}, _lead, { '@leadCompanies': linkIds });
            }

            // Extract In-part profile object if necessary
            if (isDataObject<ILeadOriginInPartCampaignProfile>(lead.originInfo?.leadOriginProfile)) {
                const inPartProfile = Object.assign({}, lead.originInfo?.leadOriginProfile);
                _lead.originInfo.leadOriginProfile = inPartProfile.dobjId;
                inPartProfiles.push(inPartProfile);
            }

            // Extract asset profile object if necessary
            if (lead?.assetProfiles?.length > 0 && isDataObject<ILeadProfile>(lead.assetProfiles[0])) {
                assetProfiles.push(...(lead.assetProfiles as any)); // Sorry

                const leadAssetProfileObjectIds = lead.assetProfiles.map((assetProfile) => {
                    return getDobjId(assetProfile);
                });
                _lead.assetProfiles = leadAssetProfileObjectIds;
            }

            // DEPRECATED: assetProfiles is now an array of numbers, not objects
            // Extract asset profile object if necessary
            const assetProfileObject = lead?.assetProfileEntity as unknown as AssetProfile;
            if (assetProfileObject?.['@type']) {
                const profileIdentity: IDataObjectIdentity & IInfogroupContainer = {
                    dobjId: lead.assetProfileEntity.dobjId,
                    queryScore: 0,
                    type: assetProfileObject?.['@type']
                };
                if (assetProfileObject?.['@type'] === DataType.LeadAssetProfile) {
                    leadAssetProfiles.push(assetProfileObject);
                } else if (assetProfileObject?.['@type'] === DataType.LeadTechnologyProfile) {
                    leadTechnologyProfiles.push(assetProfileObject);
                } else if (assetProfileObject?.['@type'] === DataType.LeadConsumerHealthProfile) {
                    leadConsumerHealthProfiles.push(assetProfileObject);
                } else if (assetProfileObject?.['@type'] === DataType.LeadCompanyProfile) {
                    leadCompanyProfiles.push(assetProfileObject);
                }
                _lead.assetProfileEntity = profileIdentity;
            }

            leads.push(_lead);
        });

        if (leads.length > 0) {
            context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: leads });
        }
        if (briefs.length > 0) {
            void context.dispatch(`${BRIEF_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: briefs }, { root: true });
        }
        if (deals.length > 0) {
            void context.dispatch(`${DEAL_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: deals }, { root: true });
        }
        if (relatedCompanies.length > 0) {
            void context.dispatch(`${LEAD_LINKED_COMPANIES_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: relatedCompanies }, { root: true });
        }
        if (inPartProfiles.length > 0) {
            void context.dispatch(`${LEAD_ORIGIN_IN_PART_CAMPAIGN_PROFILE_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: inPartProfiles }, { root: true });
        }
        if (assetProfiles.length > 0) {
            void context.dispatch(`${LEAD_PROFILE_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: assetProfiles }, { root: true });
        }

        // DEPRECATED: assetProfiles is now an array of numbers, not objects
        if (leadAssetProfiles.length > 0) {
            void context.dispatch(`${LEAD_ASSET_PROFILE_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: leadAssetProfiles }, { root: true });
        }
        // DEPRECATED: assetProfiles is now an array of numbers, not objects
        if (leadTechnologyProfiles.length > 0) {
            void context.dispatch(`${LEAD_TECHNOLOGY_PROFILE_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: leadTechnologyProfiles }, { root: true });
        }
        // DEPRECATED: assetProfiles is now an array of numbers, not objects
        if (leadConsumerHealthProfiles.length > 0) {
            void context.dispatch(
                `${LEAD_CONSUMER_HEALTH_PROFILE_MODULE}/${STORE_DATA_OBJECTS}`,
                { dataObjectList: leadConsumerHealthProfiles },
                { root: true }
            );
        }
        // DEPRECATED: assetProfiles is now an array of numbers, not objects
        if (leadCompanyProfiles.length > 0) {
            void context.dispatch(`${LEAD_COMPANY_PROFILE_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: leadCompanyProfiles }, { root: true });
        }
    }
};
